import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { notificationsApi } from "../api/collab";
import { formatRelative } from "../ds";

/** Top-bar bell: admins see how many approvals wait; everyone sees decisions on their requests. */
export function NotificationsBell({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: ["notifications"], queryFn: notificationsApi.get, refetchInterval: 60_000, retry: false });
  const seen = useMutation({ mutationFn: notificationsApi.seen, onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }) });

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const decidedUnread = (q.data?.items ?? []).filter((n) => n.unread && n.kind !== "approval_pending").length;
  const badge = isAdmin ? (q.data?.pending_approvals ?? 0) + decidedUnread : q.data?.unread ?? 0;
  if (q.isError) return null; // an older server without notifications: no bell

  return (
    <div ref={box} style={{ position: "relative" }}>
      <button
        type="button"
        className="ds-btn ds-btn--ghost ds-btn--icon"
        aria-label={badge ? `Notifications, ${badge} new` : "Notifications"}
        title="Notifications"
        onClick={() => {
          setOpen((v) => !v);
          if (!open && decidedUnread > 0) seen.mutate();
        }}
      >
        <Bell size={16} />
        {badge ? <span className="ds-badge" style={{ position: "absolute", top: 2, right: 2, minWidth: 16, height: 16, fontSize: 10 }}>{badge}</span> : null}
      </button>
      {open ? (
        <div role="dialog" aria-label="Notifications" style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", width: 340, maxHeight: 420, overflow: "auto", background: "var(--surface, #fff)", border: "1px solid var(--divider)", borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.25)", zIndex: 50 }}>
          {isAdmin && (q.data?.pending_approvals ?? 0) > 0 ? (
            <button type="button" className="ds-btn ds-btn--ghost" style={{ width: "100%", justifyContent: "flex-start" }} onClick={() => { setOpen(false); navigate("/admin/approvals"); }}>
              {q.data?.pending_approvals} request(s) waiting for approval
            </button>
          ) : null}
          {(q.data?.items ?? []).filter((n) => n.kind !== "approval_pending").length === 0 && !(isAdmin && (q.data?.pending_approvals ?? 0) > 0) ? (
            <div className="ds-small ds-muted" style={{ padding: 14 }}>You're all caught up.</div>
          ) : null}
          {(q.data?.items ?? []).filter((n) => n.kind !== "approval_pending").map((n) => (
            <div key={n.id} style={{ padding: "10px 14px", borderTop: "1px solid var(--divider)", fontWeight: n.unread ? 600 : 400 }}>
              <div style={{ fontSize: 13 }}>{n.title}</div>
              {n.detail ? <div className="ds-small ds-muted" style={{ fontWeight: 400 }}>{n.detail}</div> : null}
              <div className="ds-small ds-faint" style={{ fontWeight: 400 }}>{formatRelative(n.at)}</div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
